import { useAssetStatusList, useAssetType } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface AssetFilters {
  type: string;
  status: string;
  page: number;
}

interface AssetFiltersProps {
  onFiltersChange: (filters: AssetFilters) => void;
  onApplyFilters: (filters: AssetFilters) => void;
  onClearFilters: () => void;
  loading?: boolean;
}

export const AssetsFilterPanel = ({
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  loading = false,
}: AssetFiltersProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: assetTypeList } = useAssetType();

  const assetTypeOptions = useMemo(() => {
    return (
      assetTypeList?.result?.map((assetType: any) => ({
        value: assetType.code,
        label: assetType.name,
      })) || []
    );
  }, [assetTypeList]);

  const { data: assetStatusList } = useAssetStatusList();

  const assetStatusOptions = useMemo(() => {
    return (
      assetStatusList?.result?.map((status: any) => ({
        value: status.key,
        label: status.value,
      })) || []
    );
  }, [assetStatusList]);

  // Parse current URL parameters
  const getCurrentFiltersFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      type: searchParams.get("type") || "",
      status: searchParams.get("status") || "",
      page: 1,
    };
  }, [location.search]);

  const createFormFields = (): FormField[] => [
    {
      name: "type",
      label: "Asset Type",
      type: "select",
      required: false,
      placeholder: "All Types",
      defaultValue: getCurrentFiltersFromUrl.type,
      options: assetTypeOptions,
      classes: FIELD_CLASSES,
    },
    {
      name: "status",
      label: "Operational Status",
      type: "select",
      required: false,
      placeholder: "All Status",
      defaultValue: getCurrentFiltersFromUrl.status,
      options: assetStatusOptions,
      classes: FIELD_CLASSES,
    },
  ];

  const filterForm = FormService.createForm({
    fields: createFormFields(),
  });

  const updateUrlWithFilters = (newFilters: AssetFilters) => {
    const searchParams = new URLSearchParams(location.search);

    // Clear existing filter parameters
    const filterParams = ["type", "status"];
    filterParams.forEach((param) => searchParams.delete(param));

    // Add new filter parameters (only non-'all' values)
    Object.entries(newFilters).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
      ) {
        searchParams.set(key, String(value));
      }
    });

    // Always reset to page 1 when filters change
    searchParams.set("page", "1");

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleSubmit = (data: any) => {
    const processedData = { ...data, page: 1 };

    updateUrlWithFilters(processedData);

    onApplyFilters(processedData);
  };

  const handleReset = () => {
    const defaultFilters: AssetFilters = {
      type: "",
      status: "",
      page: 1,
    };

    filterForm.reset(defaultFilters);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });

    onClearFilters();
  };

  useEffect(() => {
    const urlFilters = getCurrentFiltersFromUrl;
    Object.keys(urlFilters).forEach((key) => {
      const value = urlFilters[key as keyof typeof urlFilters];
      if (value !== undefined) {
        filterForm.setValue(key as keyof AssetFilters, value);
      }
    });
  }, [getCurrentFiltersFromUrl, filterForm]);

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 items-center pt-6 border-t border-gray-200">
      <Button variant="outline" onClick={handleReset} disabled={loading}>
        <X className="h-4 w-4" />
        Clear Filters
      </Button>

      <Button
        isLoading={loading}
        onClick={() => handleSubmit(filterForm.getValues())}
      >
        {loading && (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={filterForm}
        onReset={handleReset}
        loading={loading}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
